import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import { Appointment, ServiceReview, ServiceReviewFormData, ReviewReply, BusinessRating } from '../types';
import toast from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';
import { X, Star, Calendar, User, CheckCircle } from 'lucide-react';

interface ServiceReviewModalProps {
  appointment: Appointment;
  isOpen: boolean;
  onClose: () => void;
  onReviewSubmitted: () => void;
}

const ServiceReviewModal: React.FC<ServiceReviewModalProps> = ({
  appointment,
  isOpen,
  onClose,
  onReviewSubmitted
}) => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState<ServiceReview[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reviewData, setReviewData] = useState<{ [key: string]: ServiceReviewFormData }>({});

  // Business rating state (new)
  const [businessRating, setBusinessRating] = useState<{ rating: number; feedback: string }>({
    rating: 0,
    feedback: ''
  });
  const [existingBusinessRating, setExistingBusinessRating] = useState<BusinessRating | null>(null);
  
  // Reply state
  const [replyingToReview, setReplyingToReview] = useState<string | null>(null);
  const [replyingToReply, setReplyingToReply] = useState<{ reviewId: string; replyId: string } | null>(null);
  const [replyMessage, setReplyMessage] = useState<string>('');
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);

  const loadExistingReviews = useCallback(async () => {
    // Check if user is authenticated
    if (!user) {
      toast.error('Please log in to view reviews');
      return;
    }

    try {
      setLoading(true);
      const existingReviews = await apiService.getAppointmentReviews(appointment.id);
      setReviews(existingReviews);
      
      // Update review data to remove services that already have reviews
      setReviewData(prev => {
        const newData = { ...prev };
        existingReviews.forEach(review => {
          delete newData[review.serviceId];
        });
        return newData;
      });
    } catch (error: any) {
      console.error('Error loading existing reviews:', error);
      
      // Check if it's an authentication error
      if (error.response?.status === 401) {
        toast.error('Authentication failed. Please log in again.');
        return;
      }
      
      toast.error('Failed to load existing reviews');
    } finally {
      setLoading(false);
    }
  }, [appointment.id, user]);

  // Load existing reviews for this appointment
  useEffect(() => {
    if (isOpen && appointment.id && user) {

      
      // Initialize review data for services that haven't been reviewed yet
      // This ensures the modal shows even if the API call fails
      const newReviewData: { [key: string]: ServiceReviewFormData } = {};
      appointment.assignments.forEach(assignment => {
        newReviewData[assignment.serviceId] = {
          appointmentId: appointment.id,
          serviceId: assignment.serviceId,
          professionalId: assignment.professionalId,
          rating: 0,
          reviewFeedback: ''
        };
      });
      setReviewData(newReviewData);

      // Reset business rating input on open; we'll attempt to load an existing one next.
      setBusinessRating({ rating: 0, feedback: '' });
      setExistingBusinessRating(null);
      
      // Then try to load existing reviews
      loadExistingReviews();

      // Also try to load an existing business rating for this appointment (if backend supports it)
      (async () => {
        try {
          const br = await apiService.getBusinessRatingForAppointment(appointment.id);
          if (br) setExistingBusinessRating(br);
        } catch (e) {
          // Non-blocking; business ratings are new and may not exist yet.
        }
      })();
    }
  }, [isOpen, appointment.id, loadExistingReviews, appointment.assignments, user]);

  const handleBusinessRatingChange = (rating: number) => {
    setBusinessRating(prev => ({ ...prev, rating }));
  };

  const handleBusinessFeedbackChange = (feedback: string) => {
    setBusinessRating(prev => ({ ...prev, feedback }));
  };

  const handleRatingChange = (serviceId: string, rating: number) => {
    setReviewData(prev => ({
      ...prev,
      [serviceId]: {
        ...prev[serviceId],
        rating
      }
    }));
  };

  const handleFeedbackChange = (serviceId: string, feedback: string) => {
    setReviewData(prev => ({
      ...prev,
      [serviceId]: {
        ...prev[serviceId],
        reviewFeedback: feedback
      }
    }));
  };

  const handleSubmitAllReviews = async () => {
    if (!user) {
      toast.error('Please log in to submit reviews');
      return;
    }

    // Build reviews to submit - need to include appointmentId, serviceId, professionalId
    const reviewsToSubmit: ServiceReviewFormData[] = Object.entries(reviewData)
      .filter(([serviceId, review]) => review.rating > 0)
      .map(([serviceId, review]) => {
        // Find the assignment to get professionalId
        const assignment = appointment.assignments.find(a => a.serviceId === serviceId);
        // Only include professionalId if it exists (backend validation doesn't accept null)
        const reviewPayload: ServiceReviewFormData = {
          appointmentId: appointment.id,
          serviceId: serviceId,
          rating: review.rating,
          reviewFeedback: review.reviewFeedback
        };
        // Only add professionalId if it exists
        if (assignment?.professionalId) {
          reviewPayload.professionalId = assignment.professionalId;
        }
        return reviewPayload;
      });
    
    const shouldSubmitBusinessRating = businessRating.rating > 0 && !existingBusinessRating;

    if (reviewsToSubmit.length === 0 && !shouldSubmitBusinessRating) {
      toast.error('Please provide a rating for the appointment or at least one service');
      return;
    }

    try {
      setSubmitting(true);
      
      // Submit business rating (optional) and service reviews (existing behavior)
      const submitPromises: Array<Promise<any>> = [];

      if (shouldSubmitBusinessRating) {
        submitPromises.push(
          apiService.createBusinessRating({
            appointmentId: appointment.id,
            rating: businessRating.rating,
            content: businessRating.feedback
          })
        );
      }

      submitPromises.push(...reviewsToSubmit.map(review => apiService.createServiceReview(review)));
      
      await Promise.all(submitPromises);
      
      // Clear the form data
      setReviewData({});
      setBusinessRating({ rating: 0, feedback: '' });
      
      // Reload reviews to show the new ones
      await loadExistingReviews();
      try {
        const br = await apiService.getBusinessRatingForAppointment(appointment.id);
        if (br) setExistingBusinessRating(br);
      } catch (e) {
        // ignore
      }
      
      const submittedCount = reviewsToSubmit.length + (shouldSubmitBusinessRating ? 1 : 0);
      toast.success(`Successfully submitted ${submittedCount} rating${submittedCount > 1 ? 's' : ''}!`);
      onReviewSubmitted();
      onClose();
    } catch (error: any) {
      console.error('Error submitting reviews:', error);
      
      if (error.response?.status === 401) {
        toast.error('Authentication failed. Please log in again.');
        return;
      }
      
      toast.error('Failed to submit reviews. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateReview = async (reviewId: string, serviceId: string) => {
    if (!user) {
      toast.error('Please log in to update a review');
      return;
    }

    const review = reviewData[serviceId];
    if (!review || review.rating === 0) {
      toast.error('Please provide a rating for this service');
      return;
    }

    try {
      setSubmitting(true);
      await apiService.updateServiceReview(reviewId, {
        rating: review.rating,
        reviewFeedback: review.reviewFeedback
      });
      
      // Reload reviews to show the updated one
      await loadExistingReviews();
      
      toast.success('Review updated successfully!');
      onReviewSubmitted();
    } catch (error: any) {
      console.error('Error updating review:', error);
      
      if (error.response?.status === 401) {
        toast.error('Authentication failed. Please log in again.');
        return;
      }
      
      toast.error('Failed to update review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteReview = async (reviewId: string) => {
    if (!user) {
      toast.error('Please log in to delete a review');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this review?')) {
      return;
    }

    try {
      setSubmitting(true);
      await apiService.deleteServiceReview(reviewId);
      
      // Reload reviews
      await loadExistingReviews();
      
      toast.success('Review deleted successfully!');
      onReviewSubmitted();
    } catch (error: any) {
      console.error('Error deleting review:', error);
      
      if (error.response?.status === 401) {
        toast.error('Authentication failed. Please log in again.');
        return;
      }
      
      toast.error('Failed to delete review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle adding a reply
  const handleAddReply = async (reviewId: string, parentReplyId?: string) => {
    if (!replyMessage.trim()) {
      toast.error('Please enter a reply message');
      return;
    }

    try {
      setIsSubmittingReply(true);
      const updatedReview = await apiService.addReviewReply(reviewId, replyMessage.trim(), parentReplyId);
      
      // Update the review in the list
      setReviews(prev => 
        prev.map(review => review.id === reviewId ? updatedReview : review)
      );
      
      // Reset state
      setReplyMessage('');
      setReplyingToReview(null);
      setReplyingToReply(null);
      toast.success('Reply added successfully');
    } catch (error: any) {
      console.error('Error adding reply:', error);
      toast.error(error.response?.data?.error || 'Failed to add reply');
    } finally {
      setIsSubmittingReply(false);
    }
  };

  // Render replies recursively
  const renderReplies = (replies: ReviewReply[], reviewId: string, depth: number = 0) => {
    if (!replies || replies.length === 0) return null;

    // Helper function to format date
    const formatReplyDate = (dateString: string) => {
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        });
      } catch {
        return dateString;
      }
    };

    return (
      <div className={`mt-3 ${depth > 0 ? 'ml-6 pl-4 border-l-2 border-gray-200' : ''}`}>
        {replies.map((reply) => (
          <div key={reply.id} className="mb-3">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-gray-900">{reply.userName}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      reply.userRole === 'owner' || reply.userRole === 'professional' 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {reply.userRole === 'owner' ? 'Business Owner' : reply.userRole === 'professional' ? 'Professional' : 'Customer'}
                    </span>
                    <span className="text-xs text-gray-500">{formatReplyDate(reply.createdAt)}</span>
                    {reply.updatedAt !== reply.createdAt && (
                      <span className="text-xs text-gray-400">(edited)</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-700">{reply.message}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-2 mt-2">
                <button
                  onClick={() => {
                    setReplyingToReply({ reviewId, replyId: reply.id });
                    setReplyingToReview(null);
                    setReplyMessage('');
                  }}
                  className="aacore-btn-outlined text-xs"
                >
                  <i className="bi bi-reply"></i> Reply
                </button>
              </div>

              {replyingToReply?.reviewId === reviewId && replyingToReply?.replyId === reply.id && (
                <div className="mt-3 space-y-2">
                  <textarea
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    rows={3}
                    placeholder="Write a reply..."
                  />
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleAddReply(reviewId, reply.id)}
                      disabled={isSubmittingReply}
                      className="aacore-btn-primary text-sm"
                    >
                      {isSubmittingReply ? 'Posting...' : 'Post Reply'}
                    </button>
                    <button
                      onClick={() => {
                        setReplyingToReply(null);
                        setReplyMessage('');
                      }}
                      disabled={isSubmittingReply}
                      className="aacore-btn-outlined text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Render nested replies */}
              {reply.replies && reply.replies.length > 0 && renderReplies(reply.replies, reviewId, depth + 1)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderStars = (rating: number, onRatingChange?: (rating: number) => void) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onRatingChange?.(star)}
            className={`transition-colors duration-200 ${
              star <= rating
                ? 'text-yellow-400 fill-yellow-400 hover:text-yellow-500 hover:fill-yellow-500'
                : 'text-gray-300 hover:text-yellow-400'
            } ${onRatingChange ? 'cursor-pointer' : 'cursor-default'}`}
            disabled={!onRatingChange || submitting}
          >
            <Star className="w-5 h-5" />
          </button>
        ))}
        {rating > 0 && (
          <span className="ml-2 text-sm text-gray-600 font-medium">
            {rating}/5
          </span>
        )}
      </div>
    );
  };

  const renderServiceCard = (assignment: any) => {
    // Ensure assignment has required properties
    if (!assignment || !assignment.serviceId) {
      return null;
    }
    
    const existingReview = reviews.find(review => review.serviceId === assignment.serviceId);
    const isReviewable = !existingReview && reviewData[assignment.serviceId];
    const isEditable = existingReview && reviewData[assignment.serviceId];

    return (
      <div key={assignment.serviceId} className="bg-white border border-gray-200 rounded-lg shadow-sm mb-4">
        <div className="p-5">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center justify-between gap-3 mb-3">
                <h3 className="text-lg font-semibold text-gray-900 flex-1 min-w-0">
                  {assignment.serviceName || 'Unknown Service'}
                </h3>
                {existingReview ? (
                  <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 flex-shrink-0">
                    Reviewed
                  </span>
                ) : (
                  <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 flex-shrink-0">
                    Not Rated
                  </span>
                )}
              </div>
              
              {assignment.professionalFirstName && (
                <div className="flex items-start gap-2">
                  <User className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-500">Professional</p>
                    <p className="text-sm text-gray-900 font-medium">
                      {assignment.professionalFirstName} {assignment.professionalLastName}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {existingReview ? (
            // Show existing review
            <div className="border-t border-gray-200 pt-4 mt-4">
              <div className="mb-4">
                <h4 className="font-medium text-gray-900">Your Review</h4>
              </div>
              
              {isEditable ? (
                // Edit mode
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rating
                      <span className="ml-2 text-xs font-normal text-gray-500">(Optional)</span>
                    </label>
                    {renderStars(reviewData[assignment.serviceId]?.rating || 0, (rating) => 
                      handleRatingChange(assignment.serviceId, rating)
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Review
                      <span className="ml-2 text-xs font-normal text-gray-500">(Optional)</span>
                    </label>
                    <textarea
                      value={reviewData[assignment.serviceId]?.reviewFeedback || ''}
                      onChange={(e) => handleFeedbackChange(assignment.serviceId, e.target.value)}
                      placeholder="Share your experience with this service..."
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900 placeholder-gray-400 resize-none"
                      rows={4}
                      maxLength={1000}
                      disabled={submitting}
                    />
                    <p className="mt-2 text-xs text-gray-500">
                      {reviewData[assignment.serviceId]?.reviewFeedback?.length || 0}/1000 characters
                    </p>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => handleUpdateReview(existingReview.id, assignment.serviceId)}
                      disabled={submitting || (reviewData[assignment.serviceId]?.rating || 0) === 0}
                      className={`aacore-btn-primary ${
                        submitting || (reviewData[assignment.serviceId]?.rating || 0) === 0 ? 'opacity-70 cursor-not-allowed' : ''
                      }`}
                    >
                      {submitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Updating...
                        </>
                      ) : (
                        'Update Review'
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setReviewData(prev => {
                          const newData = { ...prev };
                          delete newData[assignment.serviceId];
                          return newData;
                        });
                      }}
                      className="aacore-btn-outlined"
                      disabled={submitting}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                // View mode
                <div className="space-y-3">
                  {renderStars(existingReview.rating)}
                  {existingReview.reviewFeedback && (
                    <div className="bg-gray-50 rounded-md p-3 border border-gray-200">
                      <p className="text-sm text-gray-900">{existingReview.reviewFeedback}</p>
                    </div>
                  )}
                  <p className="text-xs text-gray-500">
                    Reviewed on {new Date(existingReview.createdAt).toLocaleDateString()}
                  </p>

                  {/* Replies Section */}
                  {existingReview.replies && existingReview.replies.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-gray-100">
                      <div className="text-sm font-semibold text-gray-900 mb-2">
                        Replies ({existingReview.replies.length})
                      </div>
                      {renderReplies(existingReview.replies, existingReview.id)}
                    </div>
                  )}

                  {/* Reply Input */}
                  {replyingToReview === existingReview.id && !replyingToReply && (
                    <div className="mt-4 pt-3 border-t border-gray-100 space-y-2">
                      <textarea
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        rows={3}
                        placeholder="Write a reply..."
                      />
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleAddReply(existingReview.id)}
                          disabled={isSubmittingReply}
                          className="aacore-btn-primary text-sm"
                        >
                          {isSubmittingReply ? 'Posting...' : 'Post Reply'}
                        </button>
                        <button
                          onClick={() => {
                            setReplyingToReview(null);
                            setReplyMessage('');
                          }}
                          disabled={isSubmittingReply}
                          className="aacore-btn-outlined text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Reply Button */}
                  {!replyingToReview && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <button
                        onClick={() => {
                          setReplyingToReview(existingReview.id);
                          setReplyingToReply(null);
                          setReplyMessage('');
                        }}
                        className="aacore-btn-outlined text-sm"
                      >
                        <i className="bi bi-reply"></i> Reply
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : isReviewable ? (
            // New review form
            <div className="border-t border-gray-200 pt-4 mt-4">
              <h4 className="font-medium text-gray-900 mb-4">Rate this service</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rating
                    <span className="ml-2 text-xs font-normal text-gray-500">(Optional)</span>
                  </label>
                  {renderStars(reviewData[assignment.serviceId]?.rating || 0, (rating) => 
                    handleRatingChange(assignment.serviceId, rating)
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Review
                    <span className="ml-2 text-xs font-normal text-gray-500">(Optional)</span>
                  </label>
                  <textarea
                    value={reviewData[assignment.serviceId]?.reviewFeedback || ''}
                    onChange={(e) => handleFeedbackChange(assignment.serviceId, e.target.value)}
                    placeholder="Share your experience with this service..."
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900 placeholder-gray-400 resize-none"
                    rows={4}
                    maxLength={1000}
                    disabled={submitting}
                  />
                  <p className="mt-2 text-xs text-gray-500">
                    {reviewData[assignment.serviceId]?.reviewFeedback?.length || 0}/1000 characters
                  </p>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1050] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      {/* Modal content */}
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl relative border border-gray-200 overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg mr-3">
                <Star className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Rate Your Appointment</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Appointment on {(() => {
                  const iso = (appointment as any).scheduledAt ?? (appointment as any).flexScheduledAt?.earliest;
                  return iso ? new Date(iso).toLocaleDateString() : 'Date TBD';
                })()}
                </p>
              </div>
            </div>
            <button 
              onClick={onClose} 
              className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100"
              aria-label="Close"
              disabled={submitting}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          {Object.keys(reviewData).length > 0 && (
            <div className="mt-3 p-3 bg-blue-50 rounded-md border border-blue-200">
              <p className="text-sm text-gray-700">
                <strong>{Object.keys(reviewData).length}</strong> service{Object.keys(reviewData).length > 1 ? 's' : ''} available to review. 
                Rate the services you'd like to provide feedback for.
              </p>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Business rating section (new) */}
          <div className="mb-6 p-4 rounded-lg border border-gray-200 bg-blue-50">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h4 className="text-lg font-semibold text-gray-900">Business</h4>
                <p className="text-xs text-gray-600">
                  Rate your overall experience with this business
                </p>
              </div>
              {existingBusinessRating && (
                <span className="text-xs text-gray-500">Already rated</span>
              )}
            </div>

            <div className={existingBusinessRating ? 'opacity-60 pointer-events-none' : ''}>
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rating <span className="text-red-500">*</span>
                </label>
                {renderStars(businessRating.rating, (rating) => handleBusinessRatingChange(rating))}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Review
                  <span className="ml-2 text-xs font-normal text-gray-500">(Optional)</span>
                </label>
                <textarea
                  value={businessRating.feedback}
                  onChange={(e) => handleBusinessFeedbackChange(e.target.value)}
                  placeholder="Share your overall experience..."
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900 placeholder-gray-400 resize-none"
                  rows={3}
                  maxLength={1000}
                  disabled={submitting}
                />
                <p className="mt-2 text-xs text-gray-500">
                  {businessRating.feedback.length}/1000 characters
                </p>
              </div>
            </div>
          </div>

          {/* Horizontal separator */}
          <hr className="my-6 border-gray-200" />

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading services...</p>
            </div>
          ) : appointment.assignments.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No services found for this appointment.</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600 mb-4">
                Rating individual services is optional and may be left blank.
              </p>
              <div className="space-y-4">
                {appointment.assignments.map(renderServiceCard)}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div className="text-sm text-gray-600">
              {Object.keys(reviewData).length > 0 && (
                <span>
                  {Object.keys(reviewData).length} service{Object.keys(reviewData).length > 1 ? 's' : ''} available to review
                </span>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              {Object.keys(reviewData).length > 0 && (
                <button
                  onClick={handleSubmitAllReviews}
                  disabled={submitting || (Object.values(reviewData).every(review => review.rating === 0) && (businessRating.rating === 0 || !!existingBusinessRating))}
                  className={`aacore-btn-primary w-full sm:w-auto justify-center ${
                    submitting || (Object.values(reviewData).every(review => review.rating === 0) && (businessRating.rating === 0 || !!existingBusinessRating)) ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Submit All Reviews
                    </>
                  )}
                </button>
              )}
              <button
                onClick={onClose}
                className="aacore-btn-outlined w-full sm:w-auto justify-center"
                disabled={submitting}
              >
                <X className="w-4 h-4 mr-2" />
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceReviewModal; 